import { api } from "./client";
import type { PagedResult } from "@/types/api";
import type {
  BusinessDedupCheckRequest,
  BusinessDetailDto,
  BusinessListItemDto,
  CreateBusinessRequest,
  CreateEntityRequest,
  CreatePersonRequest,
  DedupCheckResultDto,
  EntityDedupCheckRequest,
  EntityDetailDto,
  EntityListItemDto,
  PersonDedupCheckRequest,
  PersonDetailDto,
  PersonListItemDto,
  UpdateBusinessRequest,
  UpdateEntityRequest,
  UpdatePersonRequest,
} from "@/types/counterparties";

// Deal Pipeline spec §4.2 — counterparty REST endpoints. Backend code:
//   src/PhoenixCapital.Api/Controllers/Counterparties/{Persons,Entities,Businesses}Controller.cs

export interface PersonListQuery {
  page?: number;
  pageSize?: number;
  q?: string;
  state?: string;
  includeArchived?: boolean;
  archivedOnly?: boolean;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
}

export interface EntityListQuery {
  page?: number;
  pageSize?: number;
  q?: string;
  stateOfFormation?: string;
  includeArchived?: boolean;
  archivedOnly?: boolean;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
}

export interface BusinessListQuery {
  page?: number;
  pageSize?: number;
  q?: string;
  naicsCode?: string;
  hqState?: string;
  includeArchived?: boolean;
  archivedOnly?: boolean;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
}

export const personsApi = {
  list: (query?: PersonListQuery) =>
    api.get<PagedResult<PersonListItemDto>>("/persons", query),
  get: (id: string) => api.get<PersonDetailDto>(`/persons/${id}`),
  create: (body: CreatePersonRequest) =>
    api.post<PersonDetailDto>("/persons", body),
  update: (id: string, body: UpdatePersonRequest) =>
    api.put<PersonDetailDto>(`/persons/${id}`, body),
  archive: (id: string) => api.delete<void>(`/persons/${id}`),
  dedupCheck: (body: PersonDedupCheckRequest) =>
    api.post<DedupCheckResultDto>("/persons/dedup-check", body),
};

export const entitiesApi = {
  list: (query?: EntityListQuery) =>
    api.get<PagedResult<EntityListItemDto>>("/entities", query),
  get: (id: string) => api.get<EntityDetailDto>(`/entities/${id}`),
  create: (body: CreateEntityRequest) =>
    api.post<EntityDetailDto>("/entities", body),
  update: (id: string, body: UpdateEntityRequest) =>
    api.put<EntityDetailDto>(`/entities/${id}`, body),
  archive: (id: string) => api.delete<void>(`/entities/${id}`),
  dedupCheck: (body: EntityDedupCheckRequest) =>
    api.post<DedupCheckResultDto>("/entities/dedup-check", body),
};

export const businessesApi = {
  list: (query?: BusinessListQuery) =>
    api.get<PagedResult<BusinessListItemDto>>("/businesses", query),
  get: (id: string) => api.get<BusinessDetailDto>(`/businesses/${id}`),
  create: (body: CreateBusinessRequest) =>
    api.post<BusinessDetailDto>("/businesses", body),
  update: (id: string, body: UpdateBusinessRequest) =>
    api.put<BusinessDetailDto>(`/businesses/${id}`, body),
  archive: (id: string) => api.delete<void>(`/businesses/${id}`),
  dedupCheck: (body: BusinessDedupCheckRequest) =>
    api.post<DedupCheckResultDto>("/businesses/dedup-check", body),
};
